import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Select, Button, Tag } from "antd";
import { useSnackbar } from "notistack";
import {
  Calendar,
  ShieldCheck,
  Database,
  RefreshCw,
  Building2,
  Download,
  LineChart,
  ExternalLink,
} from "lucide-react";
import { getMonthlyBackupRestoreReport } from "../../utils/users";
import { useContextState } from "../../context/ContextProvider";

const { Option } = Select;

// Generate last 12 months for selector
const getLast12Months = () => {
  const months = [];
  const date = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(date.getFullYear(), date.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("default", { month: "long", year: "numeric" });
    months.push({ value, label });
  }
  return months;
};

const MonthlyReport = () => {
  const navigate = useNavigate();
  const { isLoading, getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();
  const { role } = useContextState();

  const monthOptions = getLast12Months();
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0]?.value || "");
  const [reportData, setReportData] = useState(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!isLoading && selectedMonth) {
      fetchReport(selectedMonth);
    }
  }, [isLoading, selectedMonth]);

  const fetchReport = async (month) => {
    try {
      setFetching(true);
      const token = await getIdTokenClaims();
      const res = await getMonthlyBackupRestoreReport(token.__raw, month);
      if (res.data.success) {
        setReportData(res.data);
      } else {
        enqueueSnackbar(res.data.message || "Failed to load report.", {
          variant: "error",
        });
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar("An error occurred while fetching monthly report data.", {
        variant: "error",
      });
    } finally {
      setFetching(false);
    }
  };


  const handleExportCsv = () => {
    if (!reportData?.organizations || reportData.organizations.length === 0) {
      enqueueSnackbar("No data available to export.", { variant: "info" });
      return;
    }

    const headers = [
      "Organization",
      "Platform",
      "Subscription Status",
      "Monthly Backups",
      "Records Protected",
      "Last Backup Date",
      "Backup Status",
      "Monthly Restores",
      "Completed Restores",
      "Last Restore Date",
      "Last Restore Status",
    ];

    const rows = reportData.organizations.map((org) => [
      `"${org.name.replace(/"/g, '""')}"`,
      org.platform,
      org.subscription_status === 1 ? "Active" : "Inactive",
      org.backup_count,
      org.records_backed_up,
      org.last_backup_date || "—",
      org.backup_status,
      org.restore_count,
      org.completed_restores,
      org.last_restore_date || "—",
      org.last_restore_status,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `monthly_backup_restore_report_${selectedMonth}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    enqueueSnackbar("Report exported to CSV!", { variant: "success" });
  };

  const columns = [
    {
      title: "Organization",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div
          onClick={() =>
            navigate(
              `/monthly-report/${(record.platform || "xero").toLowerCase()}/${record.id}?month=${selectedMonth}`
            )
          }
          className="cursor-pointer group py-1"
        >
          <div className="font-bold text-gray-900 group-hover:text-indigo-600 transition-colors flex items-center gap-1.5">
            <span>{name}</span>
            <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-indigo-600 transition-colors opacity-70 group-hover:opacity-100" />
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                record.platform === "Xero"
                  ? "bg-sky-50 text-sky-700 border border-sky-200"
                  : "bg-emerald-50 text-emerald-700 border border-emerald-200"
              }`}
            >
              {record.platform}
            </span>
            {record.subscription_status === 1 ? (
              <span className="text-xs text-green-600 font-medium">● Subscribed</span>
            ) : (
              <span className="text-xs text-gray-400">○ Unsubscribed</span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Backups in Month",
      dataIndex: "backup_count",
      key: "backup_count",
      align: "center",
      render: (count, record) => (
        <div>
          <span className="text-base font-bold text-gray-900">{count}</span>
          <div className="text-xs text-gray-500">
            {record.records_backed_up.toLocaleString()} records
          </div>
        </div>
      ),
    },
    {
      title: "Last Backup",
      dataIndex: "last_backup_date",
      key: "last_backup_date",
      render: (date, record) => (
        <div>
          <div className="text-sm font-medium text-gray-700">
            {date || "—"}
          </div>
          <Tag color={record.backup_status === "Active" ? "green" : "default"}>
            {record.backup_status}
          </Tag>
        </div>
      ),
    },
    {
      title: "Restores in Month",
      dataIndex: "restore_count",
      key: "restore_count",
      align: "center",
      render: (count, record) => (
        <div>
          <span className="text-base font-bold text-gray-900">{count}</span>
          <div className="text-xs text-gray-500">
            {record.completed_restores} completed
          </div>
        </div>
      ),
    },
    {
      title: "Last Restore Status",
      dataIndex: "last_restore_status",
      key: "last_restore_status",
      render: (status, record) => {
        if (!record.last_restore_date) {
          return <span className="text-gray-400 text-xs">No restores this month</span>;
        }
        const colorMap = {
          completed: "green",
          paused: "orange",
          failed: "red",
          in_progress: "blue",
          running: "blue",
        };
        return (
          <div>
            <Tag color={colorMap[status] || "default"}>
              {status.toUpperCase()}
            </Tag>
            <div className="text-xs text-gray-400 mt-0.5">
              {record.last_restore_date}
            </div>
          </div>
        );
      },
    },
    {
      title: "Analytics",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() =>
            navigate(
              `/monthly-report/${(record.platform || "xero").toLowerCase()}/${record.id}?month=${selectedMonth}`
            )
          }
          className="bg-indigo-600 hover:bg-indigo-700 border-none rounded-xl text-xs font-semibold flex items-center gap-1 mx-auto shadow-sm shadow-indigo-600/20"
        >
          <LineChart className="w-3.5 h-3.5" />
          <span>View 4 Graphs</span>
        </Button>
      ),
    },
  ];

  const summary = reportData?.summary || {
    total_orgs: 0,
    total_backups: 0,
    total_records_backed_up: 0,
    total_restores: 0,
    completed_restores: 0,
  };

  const isReseller = reportData?.is_reseller || role === "reseller";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Header & Month Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-200">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Monthly Backup &amp; Restore Report
            </h1>
            {isReseller && (
              <span className="bg-purple-100 text-purple-800 text-xs font-semibold px-2.5 py-0.5 rounded-full border border-purple-200">
                Reseller Portal
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Overview of automated cloud backups, data archives, and restore activities across all managed organisations.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <Select
              value={selectedMonth}
              onChange={(val) => setSelectedMonth(val)}
              className="w-44"
              dropdownMatchSelectWidth={false}
            >
              {monthOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>

          <Button
            type="default"
            icon={<Download className="w-4 h-4 inline mr-1" />}
            onClick={handleExportCsv}
            disabled={!reportData?.organizations?.length}
            className="flex items-center"
          >
            Export CSV
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Managed Orgs
            </span>
            <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-extrabold text-gray-900">
              {summary.total_orgs}
            </span>
            <span className="text-xs text-gray-500 block mt-0.5">Organizations Protected</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Successful Backups
            </span>
            <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-extrabold text-emerald-600">
              {summary.total_backups}
            </span>
            <span className="text-xs text-gray-500 block mt-0.5">Completed in {selectedMonth}</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Records Protected
            </span>
            <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <Database className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-extrabold text-indigo-600">
              {summary.total_records_backed_up.toLocaleString()}
            </span>
            <span className="text-xs text-gray-500 block mt-0.5">Items &amp; attachments secured</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Restores Executed
            </span>
            <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
              <RefreshCw className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-extrabold text-gray-900">
              {summary.total_restores}
            </span>
            <span className="text-xs text-emerald-600 font-medium block mt-0.5">
              {summary.completed_restores} successfully completed
            </span>
          </div>
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">
            Organization Activity Breakdown
          </h3>
          <span className="text-xs text-gray-400">
            Showing data for {selectedMonth}
          </span>
        </div>

        <Table
          columns={columns}
          dataSource={reportData?.organizations || []}
          rowKey="id"
          loading={fetching}
          pagination={{ pageSize: 15 }}
          locale={{
            emptyText: (
              <div className="py-12 text-center text-gray-400 text-sm">
                No organization backup or restore activity found for this month.
              </div>
            ),
          }}
        />
      </div>
    </div>
  );
};

export default MonthlyReport;
